import { resolver } from "@msuite/ui/libs/form";
import { useTranslation } from "@msuite/ui/localization";
import { Modal, type ModalProps } from "@msuite/ui/modal";
import { useQueryClient } from "@msuite/ui/query";
import { responseHandler } from "@msuite/ui/utils";
import {
	type GetWarehouseLocationsQuerySchema,
	type SelectWarehouseLocationsSchema,
	selectWarehouseLocationsSchema,
} from "@msuite/utils/schemas";
import { FormProvider, useForm } from "react-hook-form";
import { SelectWarehouseLocationsForm } from "@/forms/select-warehouse-locations-form";
import { client } from "@/hono";
import { getWarehouseLocationsQueryKey } from "@/hooks/use-warehouse-locations";

interface SelectWarehouseLocationsModalProps extends ModalProps {
	onComplete?: () => Promise<void>;
	initialWarehouseLocationIds: string[];
	warehouseZoneId?: string;
	query: GetWarehouseLocationsQuerySchema;
}

export function SelectWarehouseLocationsModal({
	onClose,
	isOpen,
	onComplete,
	initialWarehouseLocationIds,
	warehouseZoneId,
	query,
}: SelectWarehouseLocationsModalProps) {
	const qc = useQueryClient();
	const { t } = useTranslation();
	const methods = useForm({
		resolver: resolver(selectWarehouseLocationsSchema),
		defaultValues: {
			warehouseLocationIds: initialWarehouseLocationIds,
		},
	});

	async function handleOnSubmit(json: SelectWarehouseLocationsSchema) {
		if (query?.articleId) {
			const response = await client.stock.articles[":articleId"]["warehouse-locations"].$patch({
				param: { articleId: query.articleId },
				json,
			});
			await responseHandler(response);
		} else if (query?.warehouseZoneId) {
			const response = await client.stock["warehouse-zones"][":warehouseZoneId"][
				"warehouse-locations"
			].$patch({
				param: { warehouseZoneId: query?.warehouseZoneId },
				json,
			});
			await responseHandler(response);
		}
		await qc.refetchQueries({
			queryKey: getWarehouseLocationsQueryKey(query),
		});
		await onComplete?.();
		onClose();
	}

	return (
		<FormProvider {...methods}>
			<Modal
				title={t("stock.select_warehouse_locations")}
				isOpen={isOpen}
				onClose={onClose}
				onSubmit={methods.handleSubmit(handleOnSubmit)}
				width="90vw"
			>
				<Modal.Body>
					<SelectWarehouseLocationsForm
						initialWarehouseLocationIds={initialWarehouseLocationIds}
						warehouseZoneId={warehouseZoneId}
						query={query}
					/>
				</Modal.Body>
				<Modal.FooterForm />
			</Modal>
		</FormProvider>
	);
}
